import { useState } from 'react';
import Logo from '../components/Logo';
import api from '../api';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Shield } from 'lucide-react';

export default function Auth({ onLogin }) {
  // views: 'login', 'register', 'forgot', 'reset'
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);

  const saveToken = async (token) => {
    if (token) {
      if (Capacitor.isNativePlatform()) {
        await SecureStoragePlugin.set({ key: 'thread_token', value: token });
      } else {
        localStorage.setItem('thread_token', token);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const handleAuthSuccess = async (userData) => {
        // Check if biometric is available and not yet set on native platform
        if (Capacitor.isNativePlatform() && localStorage.getItem('thread_biometrics_enabled') === null) {
          try {
            const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
            const result = await NativeBiometric.isAvailable();
            if (result.isAvailable) {
              setTempUser(userData);
              setShowBiometricSetup(true);
              return;
            }
          } catch (bioError) {
            console.error("Biometric availability error:", bioError);
          }
        }
        onLogin(userData);
      };

      if (view === 'login') {
        const res = await api.post('/auth/login', { email, password });
        localStorage.setItem('thread_user', JSON.stringify(res.data.user));
        localStorage.setItem('thread_name', res.data.user.display_name);
        await saveToken(res.data.token);
        await handleAuthSuccess(res.data.user);
      } else if (view === 'register') {
        const res = await api.post('/auth/register', { email, password, displayName });
        localStorage.setItem('thread_user', JSON.stringify(res.data.user));
        localStorage.setItem('thread_name', res.data.user.display_name);
        await saveToken(res.data.token);
        await handleAuthSuccess(res.data.user);
      } else if (view === 'forgot') {
        await api.post('/auth/forgot-password', { email });
        setEmailSent(true);
        setView('reset');
      } else if (view === 'reset') {
        const res = await api.post('/auth/reset-password', { email, code: resetCode, newPassword: password });
        localStorage.setItem('thread_user', JSON.stringify(res.data.user));
        localStorage.setItem('thread_name', res.data.user.display_name);
        await saveToken(res.data.token);
        await handleAuthSuccess(res.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableBiometric = async () => {
    try {
      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
      await NativeBiometric.verifyIdentity({
        reason: "Confirm biometric setup",
        title: "Verify Identity",
      });
      localStorage.setItem('thread_biometrics_enabled', 'true');
      onLogin(tempUser);
    } catch (e) {
      console.error(e);
      setError('Biometric verification failed.');
    }
  };

  const handleSkipBiometric = () => {
    localStorage.setItem('thread_biometrics_enabled', 'false');
    onLogin(tempUser);
  };

  return (
    <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-color)', backgroundImage: 'var(--bg-gradient)' }}>
      <div className="glass animate-fade-in" style={{ width: '100%', maxWidth: '400px', padding: 'var(--spacing-xl)', textAlign: 'center' }}>
        
        <h2 className="text-gradient" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
          <Logo size={32} /> Thread Finance
        </h2>

        <h3 style={{ marginBottom: '1.5rem' }}>
          {view === 'login' && 'Welcome Back'}
          {view === 'register' && 'Create Account'}
          {view === 'forgot' && 'Reset Password'}
          {view === 'reset' && 'Enter Reset Code'}
        </h3>

        {error && (
          <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: 'var(--danger)', padding: '0.5rem', borderRadius: 'var(--radius-sm)', marginBottom: '1rem', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            {error}
          </div>
        )}
        
        {emailSent && view === 'reset' && (
          <div style={{ background: 'rgba(34, 197, 94, 0.1)', color: 'var(--success)', padding: '1rem', borderRadius: 'var(--radius-sm)', marginBottom: '1rem', border: '1px solid rgba(34, 197, 94, 0.3)', fontSize: '0.95rem' }}>
            <strong>Email Sent!</strong> Check your inbox for a 6-digit secure reset code.
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', textAlign: 'left' }}>
          
          {view === 'register' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-muted)' }}>Display Name</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
            </div>
          )}
          
          {(view === 'login' || view === 'register' || view === 'forgot') && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-muted)' }}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={view === 'reset'} />
            </div>
          )}
          
          {view === 'reset' && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-muted)' }}>6-Digit Reset Code</label>
              <input type="text" maxLength={6} value={resetCode} onChange={(e) => setResetCode(e.target.value)} required style={{ letterSpacing: '2px', textAlign: 'center', fontSize: '1.2rem' }} />
            </div>
          )}

          {(view === 'login' || view === 'register' || view === 'reset') && (
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', color: 'var(--text-muted)' }}>{view === 'reset' ? 'New Password' : 'Password'}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              
              {view === 'login' && (
                <div style={{ textAlign: 'right', marginTop: '0.5rem' }}>
                  <button type="button" onClick={() => { setView('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.85rem' }}>Forgot Password?</button>
                </div>
              )}
            </div>
          )}

          <button type="submit" className="btn btn-primary" style={{ marginTop: '1rem' }} disabled={loading}>
            {loading ? 'Processing...' : (
              view === 'login' ? 'Sign In' : 
              view === 'register' ? 'Register' : 
              view === 'forgot' ? 'Send Reset Code' : 
              'Reset & Login'
            )}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          {view === 'login' ? "Don't have an account? " : "Back to login? "}
          <button 
            type="button"
            onClick={() => { setView(view === 'login' ? 'register' : 'login'); setError(''); setEmailSent(false); }} 
            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 }}
          >
            {view === 'login' ? 'Sign up' : 'Log in'}
          </button>
        </p>

      </div>

      {showBiometricSetup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(2, 6, 23, 0.85)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '1.5rem'
        }}>
          <div className="glass animate-fade-in" style={{
            maxWidth: '400px',
            width: '100%',
            padding: '2.5rem 2rem',
            textAlign: 'center',
            borderRadius: 'var(--radius-lg)'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem auto',
              color: 'var(--primary)'
            }}>
              <Shield size={32} />
            </div>
            
            <h3 style={{ marginBottom: '0.75rem', fontSize: '1.5rem', color: '#ffffff' }}>Enable Biometric Login?</h3>
            <p className="text-muted" style={{ marginBottom: '2rem', fontSize: '0.95rem', lineHeight: '1.5' }}>
              Use Fingerprint or Face ID for fast and secure access next time you open the app.
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button 
                onClick={handleEnableBiometric}
                className="btn btn-primary"
                style={{ width: '100%', padding: '0.8rem' }}
              >
                Enable Biometrics
              </button>
              <button 
                onClick={handleSkipBiometric}
                className="btn btn-glass"
                style={{ width: '100%', padding: '0.8rem', border: 'none' }}
              >
                Skip for Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
